import i18n from 'i18next';
import type { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { detectLanguage, SUPPORTED_LANGUAGE_CODES } from '../lib/detectLanguage';
import de from './locales/de.json';

/**
 * German ships inside the main bundle (primary market, and the fallback language
 * i18next loads for everyone anyway); the other 13 locales are separate chunks
 * fetched on demand through this tiny backend (2026-09-05 — the 14 eagerly
 * imported JSONs were 108 of the 291 KB gzipped main bundle). `changeLanguage`
 * fetches BEFORE it emits `languageChanged`, so no call site changes and no
 * flash of raw keys; a failed chunk renders German, never `namespace.key`.
 */
type Lng = (typeof SUPPORTED_LANGUAGE_CODES)[number];
type LazyLng = Exclude<Lng, 'de'>;
type Bundle = Record<string, unknown>;

// One explicit entry per language: TypeScript fails the build when a code is
// missing, and each file becomes its own chunk (static/js/locale-xx.<hash>.chunk.js).
const loaders: Record<LazyLng, () => Promise<{ default: Bundle }>> = {
  en: () => import(/* webpackChunkName: "locale-en" */ './locales/en.json'),
  fr: () => import(/* webpackChunkName: "locale-fr" */ './locales/fr.json'),
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es.json'),
  it: () => import(/* webpackChunkName: "locale-it" */ './locales/it.json'),
  pt: () => import(/* webpackChunkName: "locale-pt" */ './locales/pt.json'),
  nl: () => import(/* webpackChunkName: "locale-nl" */ './locales/nl.json'),
  sv: () => import(/* webpackChunkName: "locale-sv" */ './locales/sv.json'),
  pl: () => import(/* webpackChunkName: "locale-pl" */ './locales/pl.json'),
  cs: () => import(/* webpackChunkName: "locale-cs" */ './locales/cs.json'),
  tr: () => import(/* webpackChunkName: "locale-tr" */ './locales/tr.json'),
  uk: () => import(/* webpackChunkName: "locale-uk" */ './locales/uk.json'),
  ja: () => import(/* webpackChunkName: "locale-ja" */ './locales/ja.json'),
  ko: () => import(/* webpackChunkName: "locale-ko" */ './locales/ko.json'),
};

const lazyBackend: BackendModule = {
  type: 'backend',
  init() { /* nothing to configure */ },
  read(lng: string, _ns: string, callback: ReadCallback) {
    const load = loaders[lng.split('-')[0] as LazyLng];
    if (!load) { callback(null, {}); return; }   // unknown code or 'de': empty bundle, German answers
    load().then(
      (m) => callback(null, m.default),
      (err) => callback(err, true),               // `true` = i18next may retry (bounded by maxRetries below)
    );
  },
};

/** Resolves once the detected language is loaded — render the app after it. */
export const i18nReady: Promise<unknown> = i18n
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    resources: { de: { translation: de } },
    partialBundledLanguages: true, // everything not in `resources` goes through lazyBackend
    // Explicit earlier choice → browser language → German (see lib/detectLanguage.ts)
    lng: detectLanguage(),
    fallbackLng: 'de',
    maxRetries: 1,
    retryTimeout: 400,
    interpolation: { escapeValue: false },
  });

/**
 * t() in a language that may not be loaded yet — replaces `i18n.getFixedT(lang)(key)`.
 * Resolves even when the chunk cannot be fetched (German text, never the raw key).
 */
export async function translateIn(lng: string, key: string): Promise<string> {
  try { await i18n.loadLanguages(lng); } catch { /* fall back to what is loaded */ }
  // i18next 25's typed t() overloads need a literal key under TS 4.9; a plain string key must go through a cast.
  return (i18n.getFixedT(lng) as unknown as (k: string) => string)(key);
}

export default i18n;
